import React, {useState} from 'react';

import {Button, Icon, Menu, SplitButton} from '@albert-io/atomic';
import {history} from 'client/history';

import {useAssignmentReleaseScope} from './hooks/useAssignmentReleaseScope';
import ReleaseGradesModal from './ReleaseGradesModal.react';

interface AssignmentGradeSplitButtonProps {
  assignmentId: string;
  assignmentName: string;
  /** Absent on All Classwork: the grading view resolves its own class and a release spans them all. */
  classroomId?: string;
  classroomName?: string;
  /** FRQ-response counts for the scope above; drive the two-state primary. */
  gradedCount: number;
  totalCount: number;
  /** Graded but not yet released to this class; informs the modal's summary. */
  releasableCount: number;
  /**
   * Every class in scope has had its grades released, so there is nothing left to release. Optional
   * and defaulted so the two unreleased states are unaffected.
   */
  isReleased?: boolean;
  /** Set when masquerading, so the scope query reads the same educator's index as the list. */
  asEducatorId?: string;
}

/**
 * Three-state grading affordance on an assignment row:
 *
 * - grade-first ("Grade" + "Release Grades" menu) while responses are ungraded;
 * - release-first ("Release Grades" + "Edit Grades" menu) once every response is graded;
 * - released — the release moves into the menu, disabled, and the primary becomes whichever
 *   grading action still applies, so the prominent slot is never a dead control.
 *
 * Meant to sit inside the row's action cluster, which already stops row-click propagation. Without a
 * `classroomId` (All Classwork) every action spans every class the teacher teaches, which is why a
 * partly-released assignment is not `isReleased`.
 */
export const AssignmentGradeSplitButton = ({
  assignmentId,
  assignmentName,
  classroomId,
  classroomName,
  gradedCount,
  totalCount,
  releasableCount,
  isReleased = false,
  asEducatorId
}: AssignmentGradeSplitButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {allClassesCounts, classroomNames, spansMultipleClasses, isScopeLoading, hasScopeError} =
    useAssignmentReleaseScope({
      assignmentId,
      asEducatorId,
      skip: !isModalOpen
    });

  // Same count the modal shows: published alone reads 0 until the release sweep runs, which would
  // leave a fully graded row offering "Grade" while its own modal reports it finished.
  const allGraded = totalCount > 0 && gradedCount + releasableCount >= totalCount;
  // Released puts the release in the menu whatever the grading state, so the primary is always
  // something the teacher can act on rather than a disabled control in the most prominent slot.
  const isReleaseFirst = allGraded && !isReleased;

  const goToGrading = () =>
    history.pushState(
      null,
      classroomId
        ? `/classes/${classroomId}/assignments/${assignmentId}/student-detail`
        : `/assignments/${assignmentId}/student-detail`
    );
  const openModal = () => setIsModalOpen(true);

  return (
    <>
      <SplitButton>
        <Button
          size='s'
          color='primary'
          variant='outlined'
          onClick={isReleaseFirst ? openModal : goToGrading}
          data-testid={`grade-${assignmentId}`}
        >
          {isReleaseFirst ? 'Release Grades' : allGraded ? 'Edit Grades' : 'Grade'}
        </Button>
        <Menu closeOnItemClick position='bottom-end' positionFixed>
          <Menu.Trigger>
            {(triggerProps) => (
              <Button
                {...triggerProps}
                size='s'
                color='primary'
                variant='outlined'
                aria-label='More grade actions'
                data-testid={`grade-actions-${assignmentId}`}
              >
                <Icon icon='caret-down' iconStyle='solid' />
              </Button>
            )}
          </Menu.Trigger>
          {/* Only the inner menu is sized; a narrowed wrapper would let it overflow rightward. */}
          <Menu.Content className='[&>.o-menu-content]:w-[174px] [&>.o-menu-content]:min-w-[174px]'>
            {isReleaseFirst ? (
              <Menu.Item leftIconProps={{icon: 'pen-to-square'}} onClick={goToGrading}>
                Edit Grades
              </Menu.Item>
            ) : (
              // `subLabel` rather than a tooltip: the item is tabIndex={-1} either way and the
              // menu's arrow-key nav skips it, so a hover tooltip would never reach a keyboard or
              // screen-reader user — the ones most in need of the explanation.
              //
              // `readonly` rather than `disabled`, matching the treatment DQA settled on for
              // AssignmentMenuOptions' "View Student Answers": `--disabled` fades the whole row to
              // 0.32 opacity, which would drop this sublabel — already `tertiary` at 13px — to
              // roughly 1.5:1 against the tray. `readonly` blocks the click without the fade, so
              // `aria-disabled`, the tertiary tone and the not-allowed cursor are set explicitly.
              <Menu.Item
                leftIconProps={{icon: 'paper-plane', color: isReleased ? 'tertiary' : undefined}}
                textProps={isReleased ? {color: 'tertiary'} : undefined}
                // `readonly` sets pointer-events: none, which would swallow the cursor too.
                className={isReleased ? 'pointer-events-auto! cursor-not-allowed!' : undefined}
                aria-disabled={isReleased || undefined}
                disableHover={isReleased}
                readonly={isReleased}
                onClick={isReleased ? undefined : openModal}
                subLabel={isReleased ? 'Grades are already released' : undefined}
              >
                Release Grades
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu>
      </SplitButton>
      {isModalOpen ? (
        <ReleaseGradesModal
          assignmentId={assignmentId}
          assignmentName={assignmentName}
          classroomId={classroomId}
          classroomName={classroomName}
          gradedCount={gradedCount}
          totalCount={totalCount}
          releasableCount={releasableCount}
          spansMultipleClasses={spansMultipleClasses}
          classroomNames={classroomNames}
          allClassesCounts={allClassesCounts}
          isScopeLoading={isScopeLoading}
          hasScopeError={hasScopeError}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default AssignmentGradeSplitButton;
